import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Button } from '../ui/Button';
import toast from 'react-hot-toast';
import { analyzeImportData, ImportConflict } from '../../services/monthlyDataImport';
import { ExcelImportPreview } from './ExcelImportPreview';
import { supabase } from '../../config/supabase';
import { Card } from '../ui/Card';

// Tipos de plantillas disponibles
export enum TemplateType {
  MONTHLY_BY_PROPERTY = 'monthly_by_property',
  MULTIPLE_PROPERTIES = 'multiple_properties',
  FULL_HISTORY = 'full_history'
}

interface MonthlyDataImporterProps {
  onImportComplete: () => void;
}

export const MonthlyDataImporter = ({ onImportComplete }: MonthlyDataImporterProps) => {
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>(TemplateType.MONTHLY_BY_PROPERTY);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [properties, setProperties] = useState<{id: string, name: string}[]>([]);
  const [previewData, setPreviewData] = useState<any[] | null>(null);
  const [conflicts, setConflicts] = useState<ImportConflict[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Por favor, selecciona un archivo Excel');
      return;
    }

    setLoading(true);

    try {
      const data = await readExcelFile(file);
      
      // Validar que los datos tengan las columnas necesarias
      const firstRow = data[0];
      
      if (!firstRow) {
        throw new Error('El archivo no contiene datos');
      }
      
      const missingFields = getRequiredFieldsForTemplate(selectedTemplate).filter(field => !Object.keys(firstRow).includes(field));
      
      if (missingFields.length > 0) {
        toast.error(`El archivo no contiene los campos requeridos: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      // Opciones según el tipo de plantilla
      const options: { propertyId?: string; month?: number; year?: number } = {};
      
      if (selectedTemplate === TemplateType.MONTHLY_BY_PROPERTY) {
        if (!selectedPropertyId) {
          toast.error('Por favor, selecciona una propiedad');
          setLoading(false);
          return;
        }
        options.propertyId = selectedPropertyId;
      } else if (selectedTemplate === TemplateType.MULTIPLE_PROPERTIES) {
        options.month = selectedMonth;
        options.year = selectedYear;
      }

      // Analizar los datos y detectar conflictos
      const { data: processedData, conflicts } = await analyzeImportData(data, selectedTemplate, options);
      
      // Guardar los datos procesados y conflictos para la vista previa
      setPreviewData(processedData);
      setConflicts(conflicts);
      setShowPreview(true);
      
    } catch (error: any) {
      console.error('Error al procesar datos:', error);
      toast.error(error.message || 'Error al procesar datos mensuales');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Obtener los datos de ejemplo según la plantilla seleccionada
    const sampleData = getSampleDataForTemplate(selectedTemplate);
    const templateName = getTemplateNameForType(selectedTemplate);

    // Crear un libro de trabajo
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sampleData);
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');

    // Descargar el archivo
    XLSX.writeFile(wb, `plantilla_${templateName}.xlsx`);
  };

  const readExcelFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet);
          
          resolve(json);
        } catch (error) {
          reject(new Error('Error al procesar el archivo Excel'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error al leer el archivo'));
      };
      
      reader.readAsBinaryString(file);
    });
  };

  // Obtiene los campos requeridos según el tipo de plantilla
  const getRequiredFieldsForTemplate = (templateType: TemplateType): string[] => {
    switch (templateType) {
      case TemplateType.MONTHLY_BY_PROPERTY:
        return ['Mes', 'Año', 'Facturación', 'Gastos Limpieza', 'Gastos InmoHost', 'Beneficio', 'Ocupación (%)'];
      
      case TemplateType.MULTIPLE_PROPERTIES:
        return ['ID Propiedad', 'Nombre Propiedad', 'Facturación', 'Gastos Limpieza', 'Gastos InmoHost', 'Beneficio', 'Ocupación (%)'];
      
      case TemplateType.FULL_HISTORY:
        return ['ID Propiedad', 'Nombre Propiedad', 'Mes', 'Año', 'Facturación', 'Gastos Limpieza', 'Gastos InmoHost', 'Beneficio', 'Ocupación (%)'];
      
      default:
        return [];
    }
  };

  // Obtiene datos de ejemplo según el tipo de plantilla
  const getSampleDataForTemplate = (templateType: TemplateType): any[] => {
    switch (templateType) {
      case TemplateType.MONTHLY_BY_PROPERTY:
        return [
          {
            'Mes': 1,
            'Año': 2025,
            'Facturación': '628 €',
            'Gastos Limpieza': '60 €',
            'Gastos InmoHost': '137 €',
            'Beneficio': '431 €',
            'Ocupación (%)': '64%',
            'Reservas': 1,
            'Precio Promedio': '22 €',
            'Duración Estancia': 18
          },
          {
            'Mes': 2,
            'Año': 2025,
            'Facturación': '0 €',
            'Gastos Limpieza': '0 €',
            'Gastos InmoHost': '0 €',
            'Beneficio': '0 €',
            'Ocupación (%)': '0%',
            'Reservas': 0,
            'Precio Promedio': '0 €',
            'Duración Estancia': 0
          }
        ];
      
      case TemplateType.MULTIPLE_PROPERTIES:
        return [
          {
            'ID Propiedad': 'prop-001',
            'Nombre Propiedad': 'Apartamento Sol',
            'Facturación': '628 €',
            'Gastos Limpieza': '60 €',
            'Gastos InmoHost': '137 €',
            'Beneficio': '431 €',
            'Ocupación (%)': '64%',
            'Reservas': 1,
            'Precio Promedio': '22 €',
            'Duración Estancia': 18
          },
          {
            'ID Propiedad': 'prop-002',
            'Nombre Propiedad': 'Villa Marina',
            'Facturación': '500 €',
            'Gastos Limpieza': '60 €',
            'Gastos InmoHost': '106 €',
            'Beneficio': '333 €',
            'Ocupación (%)': '30%',
            'Reservas': 1,
            'Precio Promedio': '50 €',
            'Duración Estancia': 10
          }
        ];
      
      case TemplateType.FULL_HISTORY:
        return [
          {
            'ID Propiedad': 'prop-001',
            'Nombre Propiedad': 'Apartamento Sol',
            'Mes': 1,
            'Año': 2025,
            'Facturación': '628 €',
            'Gastos Limpieza': '60 €',
            'Gastos InmoHost': '137 €',
            'Beneficio': '431 €',
            'Ocupación (%)': '64%',
            'Reservas': 1,
            'Precio Promedio': '22 €',
            'Duración Estancia': 18
          },
          {
            'ID Propiedad': 'prop-001',
            'Nombre Propiedad': 'Apartamento Sol',
            'Mes': 2,
            'Año': 2025,
            'Facturación': '0 €',
            'Gastos Limpieza': '0 €',
            'Gastos InmoHost': '0 €',
            'Beneficio': '0 €',
            'Ocupación (%)': '0%',
            'Reservas': 0,
            'Precio Promedio': '0 €',
            'Duración Estancia': 0
          }
        ];
      
      default:
        return [];
    }
  };

  // Obtiene el nombre de la plantilla según el tipo
  const getTemplateNameForType = (templateType: TemplateType): string => {
    switch (templateType) {
      case TemplateType.MONTHLY_BY_PROPERTY:
        return 'datos_mensuales_propiedad';
      
      case TemplateType.MULTIPLE_PROPERTIES:
        return 'multiples_propiedades';
      
      case TemplateType.FULL_HISTORY:
        return 'historico_completo';
      
      default:
        return 'datos_mensuales';
    }
  };

  // Obtiene la descripción de la plantilla según el tipo
  const getTemplateDescription = (templateType: TemplateType): string => {
    switch (templateType) {
      case TemplateType.MONTHLY_BY_PROPERTY:
        return 'Datos mensuales para una propiedad específica (varios meses)';
      
      case TemplateType.MULTIPLE_PROPERTIES:
        return 'Datos de múltiples propiedades para un mes específico';
      
      case TemplateType.FULL_HISTORY:
        return 'Histórico completo para múltiples propiedades y múltiples meses';
      
      default:
        return '';
    }
  };

  // Manejar la cancelación de la vista previa
  const handleCancelPreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    setConflicts([]);
  };

  // Manejar la finalización de la importación
  const handleImportComplete = () => {
    setShowPreview(false);
    setPreviewData(null);
    setConflicts([]);
    setFile(null);
    
    // Limpiar el input file
    const fileInput = document.getElementById('monthly-data-file') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
    
    // Notificar al componente padre
    onImportComplete();
  };

  // Generar años para el selector
  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  // Cargar propiedades al montar el componente
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const { data, error } = await supabase
          .from('properties')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setProperties(data || []);
        if (data && data.length > 0) {
          setSelectedPropertyId(data[0].id);
        }
      } catch (error: any) {
        console.error('Error al cargar propiedades:', error);
        toast.error('Error al cargar propiedades');
      }
    };

    fetchProperties();
  }, []);

  // Si estamos mostrando la vista previa, renderizar el componente de vista previa
  if (showPreview && previewData) {
    return (
      <ExcelImportPreview
        data={previewData}
        conflicts={conflicts}
        onImportComplete={handleImportComplete}
        onCancel={handleCancelPreview}
      />
    );
  }

  return (
    <Card>
      <div className="p-4">
        <h2 className="text-xl font-semibold mb-4">Importar Datos Mensuales</h2>
        
        <div className="space-y-4">
          {/* Selector de plantilla */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Selecciona el tipo de plantilla:
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value as TemplateType)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value={TemplateType.MONTHLY_BY_PROPERTY}>Datos Mensuales por Propiedad</option>
              <option value={TemplateType.MULTIPLE_PROPERTIES}>Múltiples Propiedades (un mes)</option>
              <option value={TemplateType.FULL_HISTORY}>Histórico Completo</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {getTemplateDescription(selectedTemplate)}
            </p>
          </div>
          
          {/* Opciones específicas según el tipo de plantilla */}
          {selectedTemplate === TemplateType.MONTHLY_BY_PROPERTY && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selecciona la propiedad:
              </label>
              <select
                value={selectedPropertyId}
                onChange={(e) => setSelectedPropertyId(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {selectedTemplate === TemplateType.MULTIPLE_PROPERTIES && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mes:
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value={1}>Enero</option>
                  <option value={2}>Febrero</option>
                  <option value={3}>Marzo</option>
                  <option value={4}>Abril</option>
                  <option value={5}>Mayo</option>
                  <option value={6}>Junio</option>
                  <option value={7}>Julio</option>
                  <option value={8}>Agosto</option>
                  <option value={9}>Septiembre</option>
                  <option value={10}>Octubre</option>
                  <option value={11}>Noviembre</option>
                  <option value={12}>Diciembre</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Año:
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          
          {/* Selector de archivo y botones */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="flex-grow">
              <input
                id="monthly-data-file"
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
              />
            </div>
            
            <div className="flex space-x-2">
              <Button
                type="button"
                onClick={handleDownloadTemplate}
                variant="outline"
                size="sm"
              >
                Descargar Plantilla
              </Button>
              
              <Button
                type="button"
                onClick={handleImport}
                loading={loading}
                disabled={!file}
                size="sm"
              >
                Importar
              </Button>
            </div>
          </div>
          
          {file && (
            <div className="text-sm text-gray-500">
              Archivo seleccionado: <span className="font-medium">{file.name}</span>
            </div>
          )}
          
          <div className="text-xs text-gray-500 mt-2 p-3 bg-gray-50 rounded-md">
            <p className="font-medium mb-1">Instrucciones:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Selecciona el tipo de plantilla que deseas utilizar</li>
              <li>Descarga la plantilla de Excel haciendo clic en "Descargar Plantilla"</li>
              <li>Completa los datos en la plantilla siguiendo el formato indicado</li>
              <li>Guarda el archivo y súbelo utilizando el selector de archivos</li>
              <li>Haz clic en "Importar" para cargar los datos</li>
            </ol>
          </div>
        </div>
      </div>
    </Card>
  );
};
